import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { closestTo } from "date-fns";
import { IconButton, TimeSlotButton } from "../.";
import { TimeSlotButtonStyles } from "../../types/styles";
import { ArrowLeftSolid, ArrowRightSolid } from "../../assets/icons";
import { AvailabilitySearchTimeSlot } from "../../domain_objects/AvailabilitySearchResponse";
import DateHelper from "../../helpers/DateHelper";
import { TimeSlot } from "../../types";

export const TimeSlotSelectorContainer = styled.div`
  display: grid;
  grid-template-columns: 32px auto 32px;
  column-gap: 10px;
  align-items: center;
`;

export const TimeSlotContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(50px, auto));
  column-gap: 10px;
  justify-content: center;
`;

export type TimeSlotSelectorProps = {
  initialTimeSlot: TimeSlot;
  micrositeName: string;
  times: Array<AvailabilitySearchTimeSlot>;
  selectedTime: string | null;
  setSelectedTime: React.Dispatch<React.SetStateAction<string | null>>;
  styles: TimeSlotButtonStyles;
};

export const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  initialTimeSlot,
  micrositeName,
  times,
  selectedTime,
  setSelectedTime,
  styles,
}) => {
  const { t } = useTranslation();
  const timeSlots: TimeSlot[] = times.map((time) => {
    return { MicrositeName: micrositeName, TimeSlot: time };
  });
  const timeSlotDates: Date[] = timeSlots.map(
    (time) => DateHelper.parseTime(DateHelper.sliceTimeSlot(time))!
  );
  const initialTimeSlotDate = DateHelper.parseTime(
    DateHelper.sliceTimeSlot(initialTimeSlot)
  );
  const closestTimeDate = closestTo(initialTimeSlotDate!, timeSlotDates);

  const timeSlotDatesISO = timeSlotDates.map((date) => date.toISOString());
  const closestTimeISO = closestTimeDate?.toISOString();
  const closestTimeIndex = timeSlotDatesISO.indexOf(closestTimeISO!);

  const initialListPosition = Math.max(
    0,
    Math.min(closestTimeIndex - 1, times.length - 3)
  );
  const [listPosition, setListPosition] = useState<number>(initialListPosition);
  const formattedTimes = DateHelper.sliceAvailabilitySearchTimeSlots(times);

  const canNavigateLeft = listPosition > 0;
  const canNavigateRight = listPosition < formattedTimes.length - 3;

  useEffect(() => {
    if (listPosition < 0) {
      setListPosition(0);
    } else if (listPosition > formattedTimes.length - 3) {
      setListPosition(Math.max(0, formattedTimes.length - 3));
    }
  }, [listPosition, formattedTimes]);

  const handleSelectedTime = (time: string) => {
    setSelectedTime(selectedTime === time ? null : time);
  };

  return (
    <TimeSlotSelectorContainer data-id="time-slot-selector">
      <IconButton
        ariaLabel={t("NavigateListToEarlierTimeSlot")}
        onClick={() => setListPosition((prev) => prev - 1)}
        icon={ArrowLeftSolid}
        borderVisible
        disabled={!canNavigateLeft}
        dataId={`navigate-left-button-${micrositeName}`}
      />
      <TimeSlotContainer>
        {formattedTimes
          .slice(listPosition, listPosition + 3)
          .map((time, index) => (
            <TimeSlotButton
              key={index}
              text={time}
              isSelected={selectedTime === time}
              onClick={() => handleSelectedTime(time)}
              styles={styles}
            />
          ))}
      </TimeSlotContainer>
      <IconButton
        ariaLabel={t("NavigateListToLaterTimeSlot")}
        onClick={() => setListPosition((prev) => prev + 1)}
        icon={ArrowRightSolid}
        borderVisible
        disabled={!canNavigateRight}
        dataId={`navigate-right-button-${micrositeName}`}
      />
    </TimeSlotSelectorContainer>
  );
};

export default TimeSlotSelector;
